import type { IndexedArray } from './stateTypes'

export * from './loadingMask'
export * from './jwt'

export const loadJs = (url: string): Promise<Event> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    // script.charset = 'utf-8'
    // script.timeout = 120
    script.type = 'text/javascript'
    script.onload = resolve
    script.onerror = reject
    script.src = url
    const container = document.readyState === 'loading' ? document.head : document.body
    container.appendChild(script)
  })
}

export const loadCss = (url: string): Promise<Event> => {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link')
    link.type = 'text/css'
    link.rel = 'stylesheet'
    link.onload = resolve
    link.onerror = reject
    link.href = url
    document.head.appendChild(link)
  })
}

export const isNonemptyArray = (arr: Array<any> | null | undefined | any): arr is NonNullable<Array<any>> => Array.isArray(arr) && arr.length > 0

export const ensureArray = <T = any>(thing: T | T[] | null | undefined): T[] => {
  if (Array.isArray(thing)) {
    return thing
  }
  if (thing == null) {
    return []
  }
  return [thing]
}

export interface IndexArray<T> extends IndexedArray<T> {
  _map: Map<any, any>,
  reIndex(): IndexArray<T>,
}

export const indexArray = <T = any>(arr: Array<T>, key: string | Function, options: { clone?: boolean, cached?: boolean } = {}): IndexArray<T> => {
  const { clone, cached = true } = options
  if (cached && Array.isArray(arr) && ('_map' in arr) && ('get' in arr)) {
    return arr as IndexArray<any>
  }
  const array = Array.isArray(arr) ? arr : []
  const reIndex = () => indexArray(array, key, { clone: false, cached: false })
  if (!isNonemptyArray(array)) {
    return Object.defineProperties(array, {
      _map: { configurable: true, value: new Map() },
      has: { configurable: true, value: () => false },
      get: { configurable: true, value: () => undefined },
      reIndex: { configurable: true, value: reIndex },
    }) as IndexArray<any>
  }
  const isFn = typeof key === 'function'
  const map = new Map(array.map(a => [isFn ? key(a, array) : (a as any)[key], a]))
  return Object.defineProperties(clone ? [...array] : array, {
    _map: { configurable: true, value: map },
    has: { configurable: true, value: (id: any) => map.has(id) },
    get: { configurable: true, value: (id: any) => map.get(id) },
    reIndex: { configurable: true, value: reIndex },
  }) as IndexArray<T>
}

export const throwErrorFn = (err: any) => {
  throw err
}

export const tryParseErrorJson = (text: string | undefined) => {
  if (text && /^[{["]/.test(text.trim())) {
    try {
      return JSON.parse(text)
    }
    catch (e) {
      return text
    }
  }
  return text
}

export type DedupPromiseOptions = {
  keepPromiseIn?: number
}
export type DedupPromiseFn = <T = any>(uniqueKey: string, fn: () => Promise<T>, options?: DedupPromiseOptions) => Promise<T>
export type DedupPromise = DedupPromiseFn & {
  map: Map<string, Promise<any>>
  clear: () => void
  remove: (key: string) => void
}
const dedupPromiseMap = new Map<string, Promise<any>>()
const dedupPromiseFn: DedupPromiseFn = (uniqueKey, fn, options) => {
  if (!uniqueKey) {
    return Promise.reject(new Error('key is required'))
  }
  if (typeof fn !== 'function') {
    return Promise.reject(new Error('fn must be a function'))
  }
  const cachedPromise = dedupPromiseMap.get(uniqueKey)
  if (cachedPromise) {
    return cachedPromise
  }
  const promise = fn()
  dedupPromiseMap.set(uniqueKey, promise)
  Promise.resolve(promise).then(() => {
    const keepPromiseIn = options?.keepPromiseIn || 0
    if (!keepPromiseIn) {
      dedupPromiseMap.delete(uniqueKey)
    }
    else if (keepPromiseIn > 0 && Number.isFinite(keepPromiseIn)) {
      setTimeout(() => {
        dedupPromiseMap.delete(uniqueKey)
      }, keepPromiseIn)
    }
    // else keep promise forever
  }, () => {
    dedupPromiseMap.delete(uniqueKey) // always immediately remove promise on error
  })
  return promise
}
export const dedupPromise = Object.defineProperties(dedupPromiseFn, {
  map: { value: dedupPromiseMap },
  clear: { value: () => dedupPromiseMap.clear() },
  remove: { value: (key: string) => dedupPromiseMap.delete(key) },
}) as DedupPromise

export const uniq = (arr: Array<any>): Array<any> => (!arr || !arr.length ? [] : Array.from(new Set(Array.isArray(arr) ? arr : Array.from(arr))))

export const createTimeoutPromise = (timeout: number, errorMsg?: string): [Promise<never>, () => void] => {
  let ts: any
  const timeoutPromise = new Promise((_, reject) => {
    ts = setTimeout(() => {
      ts = undefined
      reject(new Error(errorMsg || `${timeout}ms timeout`))
    }, timeout)
  }) as Promise<never>
  const clear = () => {
    if (ts) {
      clearTimeout(ts)
      ts = undefined
    }
  }

  return [timeoutPromise, clear]
}

export const wait = (ms: number) => new Promise(r => setTimeout(r, ms))

export function noop() { }
